import React from "react";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import cx from "classnames";
import { NavigateLink } from "components/Navigate-link/NavigateLink";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useFormatNumericHook } from "hooks";
import { useAllowDealTabOpenSelector } from "store";

import "./MeasuresTable.styles.scss";

interface TableCellProps {
  value: string | number | null;
  postfix?: string;
  aggregated?: boolean;
  dealId?: number;
  formatDate?: boolean;
  id?: string;
  indentation?: number;
  abbreviated?: boolean;
  decimal?: number;
  overridesTooltip?: string;
  // Per-column flag from the API. When defined it is the authority on whether the
  // value is run through the number format; when undefined we fall back to the
  // legacy postfix-driven behavior.
  applyNumberFormat?: boolean;
}

// e.g. 2023-06-03T00:00:00
const ISO_REGEX = /\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d/;

export const TableCell = ({
  value,
  postfix,
  dealId,
  aggregated,
  formatDate,
  id,
  indentation = 0,
  abbreviated,
  decimal,
  overridesTooltip,
  applyNumberFormat
}: TableCellProps) => {
  if (value === null || value === undefined) {
    return "-";
  }

  const title = formatDate && ISO_REGEX.test(value.toString()) ? Intl.DateTimeFormat().format(new Date(value)) : value;

  const postfixSetting = postfix
    ? id === "calcField-pme" || id === "calcField-irr" || postfix === "%"
      ? "postfix"
      : "prefix"
    : "none";
  return (
    <div
      style={{ paddingLeft: indentation * 8, minWidth: 0 }}
      className={cx("measures-table__cell", { aggregated, is_deal: !!dealId })}
    >
      <CellContent
        text={title.toString()}
        postfix={postfix}
        dealId={dealId}
        postfixSetting={postfixSetting}
        abbreviated={abbreviated}
        decimal={decimal}
        overridesTooltip={overridesTooltip}
        applyNumberFormat={applyNumberFormat}
      />
    </div>
  );
};

const CellContent = ({
  text,
  postfix,
  dealId,
  postfixSetting,
  abbreviated,
  decimal,
  overridesTooltip,
  applyNumberFormat
}: {
  text: string;
  postfixSetting: "postfix" | "prefix" | "none";
  postfix?: string;
  dealId?: number;
  abbreviated?: boolean;
  decimal?: number;
  overridesTooltip?: string;
  applyNumberFormat?: boolean;
}) => {
  const allowNewTab = useAllowDealTabOpenSelector();
  const formatNumeric = useFormatNumericHook();

  if (!text) {
    return "-";
  }

  if (dealId) {
    return (
      <MaterialTooltip title={text} isShow>
        <span
          style={{
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            overflow: "hidden"
          }}
        >
          <NavigateLink
            allowNewTab={allowNewTab}
            route="dealReport"
            params={{ deal_id: String(dealId) }}
            style={{
              color: "#26a6ae"
            }}
          >
            {text}
          </NavigateLink>
        </span>
      </MaterialTooltip>
    );
  }

  // The API's `apply_number_format` flag is the authority when present; otherwise
  // keep the legacy behavior of formatting only when a unit postfix is shown.
  const shouldFormat = applyNumberFormat ?? postfixSetting !== "none";
  const display = shouldFormat ? formatNumeric({ value: text, format: { abbreviated, decimal } }) : text;

  return (
    <>
      <OverridesTooltip overridesTooltip={overridesTooltip} />
      {postfixSetting === "prefix" && postfix?.trim() && <div>{postfix}</div>}
      <div>{display}</div>
      {postfixSetting === "postfix" && postfix?.trim() && <div>{postfix}</div>}
    </>
  );
};

const OverridesTooltip = ({ overridesTooltip }: { overridesTooltip?: string }) => {
  return (
    overridesTooltip && (
      <MaterialTooltip title={overridesTooltip} isShow>
        <InfoOutlinedIcon sx={{ fontSize: "15px" }} />
      </MaterialTooltip>
    )
  );
};
